import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth interceptor — sessionStorage prevents token theft via persistent XSS
export const setAuthToken = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    sessionStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    sessionStorage.removeItem('token');
  }
};

// Initialize auth from sessionStorage
const token = sessionStorage.getItem('token');
if (token) {
  setAuthToken(token);
}

// Auth API
export const authAPI = {
  signup: (data) => axios.post(`${API}/auth/signup`, data),
  login: (data) => axios.post(`${API}/auth/login`, data),
  getMe: () => axios.get(`${API}/auth/me`),
};

// Goals API
export const goalsAPI = {
  getAll: () => axios.get(`${API}/action-plans`),
  create: (data) => axios.post(`${API}/action-plans`, data),
  update: (id, data) => axios.patch(`${API}/action-plans/${id}`, data),
  delete: (id) => axios.delete(`${API}/action-plans/${id}`),
};

// Content Library API
export const contentAPI = {
  // Learning Paths
  getPaths: () => axios.get(`${API}/content/paths`),
  getPath: (pathCategory) => axios.get(`${API}/content/path/${pathCategory}`),
  getTopicLessons: (topicSlug) => axios.get(`${API}/content/topic/${topicSlug}`),
  updateProgress: (contentId, data) => axios.post(`${API}/content/progress/${contentId}`, null, { params: data }),
  
  // Legacy/Browse
  getTopics: () => axios.get(`${API}/content/topics`),
  getAll: (topic, contentType, pathCategory) => {
    const params = new URLSearchParams();
    if (topic) params.append('topic', topic);
    if (contentType) params.append('content_type', contentType);
    if (pathCategory) params.append('path_category', pathCategory);
    return axios.get(`${API}/content?${params.toString()}`);
  },
  getById: (id) => axios.get(`${API}/content/${id}`),
  getRecommendations: () => axios.get(`${API}/content/recommendations/for-you`),
  
  // Admin - Content Management
  create: (data) => axios.post(`${API}/content`, data),
  update: (id, data) => axios.patch(`${API}/content/${id}`, data),
  delete: (id) => axios.delete(`${API}/content/${id}`),
  seed: () => axios.post(`${API}/content/seed`),
  
  // CLaiR Content Studio
  generateContent: (data) => axios.post(`${API}/content/generate`, data),
  saveDraft: (data) => axios.post(`${API}/content/save-draft`, data),
  submitAssessment: (contentId, answers) => axios.post(`${API}/content/${contentId}/assessment`, { answers }),
  updateContent: (contentId, data) => axios.patch(`${API}/content/${contentId}`, data),
  reorderContent: (items) => axios.put(`${API}/content/reorder`, items),
  generateTTS: (contentId) => axios.post(`${API}/content/${contentId}/tts`, {}, { responseType: 'blob' }),
};

// Journal API
export const journalAPI = {
  getAll: () => axios.get(`${API}/journal`),
  create: (data) => axios.post(`${API}/journal`, data),
  detectMood: (journalText) => axios.post(`${API}/journal/detect-mood`, { journal_text: journalText }),
};

// Action Plans API
export const actionPlansAPI = {
  getAll: () => axios.get(`${API}/action-plans`),
  create: (data) => axios.post(`${API}/action-plans`, data),
};

// Coach Chat API
export const coachAPI = {
  chat: (message) => axios.post(`${API}/coach/chat`, { message }),
  getHistory: () => axios.get(`${API}/coach/history`),
  deleteSession: () => axios.delete(`${API}/coach/session`),
  getInsights: () => axios.get(`${API}/coach/insights`),
};

// Insight Tracker API (Relational Memory)
export const insightsAPI = {
  getAll: () => axios.get(`${API}/insights`),
  createItem: (category, text) => axios.post(`${API}/insights/item`, { category, text }),
  updateItem: (category, itemId, text) => axios.put(`${API}/insights/item/${category}/${itemId}`, { text }),
  deleteItem: (category, itemId) => axios.delete(`${API}/insights/item/${category}/${itemId}`),
  analyze: () => axios.post(`${API}/insights/analyze`),
  validateSuggestion: (suggestionId, accept) => axios.post(`${API}/insights/validate`, { suggestion_id: suggestionId, accept }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => axios.get(`${API}/dashboard/stats`),
  getUpcomingSessions: () => axios.get(`${API}/dashboard/upcoming-sessions`),
};

// Subscription API
export const subscriptionAPI = {
  getPackages: () => axios.get(`${API}/subscription/packages`),
  getUsage: () => axios.get(`${API}/subscription/usage`),
  createCheckout: (packageId, originUrl) => 
    axios.post(`${API}/subscription/checkout`, { package_id: packageId, origin_url: originUrl }),
  getCheckoutStatus: (sessionId) => axios.get(`${API}/subscription/checkout/status/${sessionId}`),
};

// Small Groups API
export const groupsAPI = {
  // Groups
  getAll: () => axios.get(`${API}/groups`),
  getMyGroups: () => axios.get(`${API}/groups/my`),
  getById: (groupId) => axios.get(`${API}/groups/${groupId}`),
  create: (data) => axios.post(`${API}/groups`, data),
  join: (groupId) => axios.post(`${API}/groups/${groupId}/join`),
  leave: (groupId) => axios.post(`${API}/groups/${groupId}/leave`),
  
  // Posts
  getPosts: (groupId) => axios.get(`${API}/groups/${groupId}/posts`),
  createPost: (groupId, data) => axios.post(`${API}/groups/${groupId}/posts`, data),
  addReply: (groupId, postId, content) => axios.post(`${API}/groups/${groupId}/posts/${postId}/replies`, { content }),
  reactToPost: (groupId, postId, reactionType) => axios.post(`${API}/groups/${groupId}/posts/${postId}/react`, { reaction_type: reactionType }),
  
  // Meetups
  getMeetups: (groupId) => axios.get(`${API}/groups/${groupId}/meetups`),
  createMeetup: (groupId, data) => axios.post(`${API}/groups/${groupId}/meetups`, data),
  rsvp: (groupId, meetupId, status) => axios.post(`${API}/groups/${groupId}/meetups/${meetupId}/rsvp`, { status }),
};

// Facilitator API
export const facilitatorAPI = {
  getStatus: () => axios.get(`${API}/facilitator/status`),
  apply: (data) => axios.post(`${API}/facilitator/apply`, data),
  accept: () => axios.post(`${API}/facilitator/accept`),
  getApplications: () => axios.get(`${API}/facilitator/applications`),
  reviewApplication: (applicationId, approved, adminMessage = '', adminNotes = '') => 
    axios.post(`${API}/facilitator/applications/${applicationId}/review?approved=${approved}&admin_message=${encodeURIComponent(adminMessage)}&admin_notes=${encodeURIComponent(adminNotes)}`),
  getFacilitators: () => axios.get(`${API}/facilitator/list`),
  revoke: (userId, reason = '') => axios.post(`${API}/facilitator/${userId}/revoke?reason=${encodeURIComponent(reason)}`),
  reinstate: (userId) => axios.post(`${API}/facilitator/${userId}/reinstate`),
  suspend: (userId, returnDate, reason = '') => 
    axios.post(`${API}/facilitator/${userId}/suspend?return_date=${encodeURIComponent(returnDate)}&reason=${encodeURIComponent(reason)}`),
  endSuspension: (userId) => axios.post(`${API}/facilitator/${userId}/end-suspension`),
};

// Groups extended API
export const groupsExtendedAPI = {
  update: (groupId, data) => axios.put(`${API}/groups/${groupId}`, data),
  launch: (groupId) => axios.post(`${API}/groups/${groupId}/launch`),
};

// Reports API
export const reportsAPI = {
  submit: (data) => axios.post(`${API}/reports`, data),
  getAll: () => axios.get(`${API}/reports`),
  resolve: (reportId, status, adminNotes = '') => 
    axios.post(`${API}/reports/${reportId}/resolve?status=${status}&admin_notes=${encodeURIComponent(adminNotes)}`),
  getSupportEmail: () => axios.get(`${API}/support-email`),
};

// Onboarding API
export const onboardingAPI = {
  getStatus: () => axios.get(`${API}/onboarding/status`),
  updateStep: (step) => axios.post(`${API}/onboarding/step`, { step, completed: true }),
  dismiss: () => axios.post(`${API}/onboarding/dismiss`),
  reset: () => axios.post(`${API}/onboarding/reset`),
};

// Profile API
export const profileAPI = {
  update: (data) => axios.put(`${API}/auth/profile`, data),
  getCrisisResources: () => axios.get(`${API}/crisis-resources`),
};

// Coaching Application API
export const coachingAPI = {
  apply: (data) => axios.post(`${API}/coaching/apply`, data),
  getApplications: () => axios.get(`${API}/coaching/applications`),
  updateApplication: (id, status, admin_notes = '') => axios.patch(`${API}/coaching/applications/${id}`, { status, admin_notes }),
};

